import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { GATES } from "@/lib/gates";
import { GateProgressMap } from "@/components/site/GateProgressMap";
import { listGateProgress, type GateProgressRow } from "@/lib/gates.functions";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, LogIn } from "lucide-react";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/gates/map")({
  head: () => ({
    meta: [
      { title: "The Life Journey Map — 47 Gates · KAfrequency" },
      { name: "description", content: "Preview the constellation of the 47 Gates of Consciousness. Sign in to illuminate your own path as you walk it." },
      { property: "og:title", content: "The Life Journey Map — 47 Gates" },
      { property: "og:description", content: "A living constellation of forty-seven doorways. Walk them at your own pace; your path lights up as you go." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/gates/map") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/gates/map") }],
  }),
  component: PublicJourneyMap,
});

function PublicJourneyMap() {
  const load = useServerFn(listGateProgress);
  const [authState, setAuthState] = useState<"unknown" | "signed-out" | "signed-in">("unknown");
  const [progress, setProgress] = useState<GateProgressRow[]>([]);
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(({ data }) => {
      if (!active) return;
      if (data.user) {
        setAuthState("signed-in");
        load()
          .then((rows) => active && setProgress(rows))
          .catch(() => active && setProgress([]));
      } else {
        setAuthState("signed-out");
      }
    });
    return () => {
      active = false;
    };
  }, [load]);

  const completedCount = progress.filter((p) => p.completed_at).length;

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
      <header className="mx-auto max-w-3xl text-center">
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">The Life Journey Map</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight sm:text-5xl">
          Forty-seven doorways.<br />
          <span className="text-gold-luminous">One illuminated path.</span>
        </h1>
        <p className="mt-6 text-base text-muted-foreground sm:text-lg">
          Each point is a Gate. Walk them in order or follow the one calling loudest today —
          the constellation lights up as your practice deepens.
        </p>
      </header>

      <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card className="relative overflow-hidden p-0">
          <GateProgressMap progress={progress} reduceMotion={reduceMotion} />
          {authState === "signed-out" && (
            <div className="pointer-events-none absolute inset-0 flex items-end justify-center bg-gradient-to-t from-obsidian/90 via-obsidian/20 to-transparent p-6">
              <div className="pointer-events-auto max-w-sm rounded-xl border hairline bg-obsidian/80 p-4 text-center backdrop-blur">
                <p className="text-sm text-foreground/80">
                  This is the shape of the path. Sign in to illuminate your own Gates as you walk them.
                </p>
                <Button asChild size="sm" className="mt-3">
                  <Link to="/auth">
                    <LogIn size={14} className="mr-1" /> Sign in to begin
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex items-center gap-2 text-sm text-gold-luminous">
              <Sparkles size={14} />
              <span>{authState === "signed-in" ? "Your constellation" : "Preview"}</span>
            </div>
            <p className="mt-2 text-sm text-foreground/70">
              {authState === "signed-in"
                ? completedCount === 0
                  ? "Your path is ready. Begin at the First Gate — each Gate you walk illuminates the next."
                  : `${completedCount} of 47 walked. Return often; the map deepens with practice.`
                : "Signed-out visitors see the order of the Gates. Your own progress — which Gates glow, which is current — appears once you sign in."}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline">
                <Link to="/gates/$n" params={{ n: "1" }}>
                  Begin at Gate 1
                </Link>
              </Button>
              <Button asChild size="sm" variant="ghost">
                <Link to="/gates">See all 47</Link>
              </Button>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="font-serif text-lg text-ivory">The order of the Gates</h2>
            <ol className="mt-3 max-h-80 space-y-1 overflow-y-auto pr-2 text-sm">
              {GATES.slice(0, 12).map((g) => (
                <li key={g.n}>
                  <Link
                    to="/gates/$n"
                    params={{ n: String(g.n) }}
                    className="flex items-baseline justify-between rounded-md px-2 py-1 hover:bg-gold/5 hover:text-gold-luminous"
                  >
                    <span className="text-foreground/80">
                      <span className="text-gold">Gate {String(g.n).padStart(2, "0")}</span> · {g.title}
                    </span>
                  </Link>
                </li>
              ))}
              <li className="px-2 pt-2 text-xs text-foreground/50">
                …and 35 more.{" "}
                <Link to="/gates" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
                  See the full sequence →
                </Link>
              </li>
            </ol>
          </Card>

          <div className="grid gap-2 text-xs text-foreground/70">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-gold" /> Walked
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#fff3c4]" /> Current gate
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-gold/30" /> Ahead
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
